import { type AnalyticsPlacementTypes, sendEvent, setAnalyticsTrigger } from '@core/analytics'
import { DIALOG_IDS, modalStore } from '@core/modal'
import { useActions } from '@core/store'
import { useAuthModal } from '@features/auth'
import { useCurrentUser } from '@features/session'

import { downloadDialogStore, type TDownloadAudio } from '../store/downloadDialogStore'

/**
 * Imperative open/close seam for the shared download dialog. `open(audio, placement)` fires
 * download_button_click (with the caller's placement, always — parity with audio-web), then either
 * redirects a guest to sign-in (auth-drawer + post-login resume deferred to MI2-2038) or seeds the
 * subject and opens the dialog. The download_sound event + gift-after-download gate live in the
 * container, so they aren't tied to the opener.
 */
export const useDownloadDialog = () => {
    const { open: openAuthModal } = useAuthModal()
    const { id: userId } = useCurrentUser()
    const actions = useActions({
        setAudio: downloadDialogStore.actions.setAudio,
        openModal: modalStore.actions.open,
        closeModal: modalStore.actions.close,
    })

    const resume = (audio: TDownloadAudio) => {
        actions.setAudio(audio)
        actions.openModal(DIALOG_IDS.download)
    }

    const open = (audio: TDownloadAudio, placement: AnalyticsPlacementTypes) => {
        sendEvent({
            name: 'download_button_click',
            payload: {
                audio: audio.id,
                params__key: ['player_placement'],
                params__str_value: [placement],
            },
        })

        if (userId === 0) {
            setAnalyticsTrigger('download_click')
            openAuthModal({
                greetingKey: 'download',
                intent: { kind: 'download', audio, placement },
            })

            return
        }

        resume(audio)
    }

    const close = () => {
        actions.closeModal()
        actions.setAudio(null)
    }

    return { open, resume, close }
}
